import { Button, Dialog, MenuItem, TextField } from "@mui/material";
import { useState } from "react";
import { useSnackbar } from "notistack";
import AutocompleteField from "elements/AutocompleteField";
import { ApiRequest } from "GlobalFunctions";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (client: any) => void;
}

export default function InvoiceClientDialog({ open, onClose, onCreated }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = {
    client_id: "null",
    client_name: "",
    client_name2: "",
    client_last_name: "",
    client_last_name2: "",
    business_name: "",
    document: "",
    document_type: "13",
    document_dv: "",
    person_type: "2",
    tax_regime: "O-99",
    tax_responsible: "0",
    client_email: "",
    client_phone: "",
    client_address: "",
    location_region_id: "",
    location_city_id: "",
    postal_code: "",
  };

  const [form, setForm] = useState(defaultValues);

  const handleChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const calcDV = (nit: string): string => {
    const cleaned = nit.replace(/[^0-9]/g, '');
    if (!cleaned) return '';
    const primes = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];
    let sum = 0;
    for (let i = 0; i < cleaned.length; i++) {
      sum += parseInt(cleaned[cleaned.length - 1 - i]) * primes[i];
    }
    const mod = sum % 11;
    return mod >= 2 ? String(11 - mod) : String(mod);
  };

  const handleSubmit = async () => {
    if (!form.document || !form.client_name || !form.client_email) {
      enqueueSnackbar("Nombre, documento y email son requeridos", { variant: "error" });
      return;
    }

    await ApiRequest({
      url: "/clients/addedit",
      method: "POST",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: form,
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar("Cliente creado", { variant: "success" });
          ApiRequest({
            url: "/clients/details",
            method: "get",
            headers: { ltkn: localStorage.getItem("ltkn") },
            query: { client_id: response.client_id },
            setResponse: (res: any) => {
              onCreated(res.data);
              onClose();
              setForm(defaultValues);
            },
          });
        } else {
          enqueueSnackbar(response.message || "Error al crear cliente", { variant: "error" });
        }
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <div className="DialogContainer">
        <div className="ModuleWrapper">
          <div className="boxContainerHeader">
            <div className="boxContainerTitle">
              <h2 className="h2_title">Nuevo Cliente</h2>
              <span className="h2_subtitle">Datos para facturación electrónica</span>
            </div>
          </div>

          <div className="input_group">
            <h2>Identificación</h2>
            <TextField
              name="document_type" label="Tipo documento" select fullWidth
              value={form.document_type}
              onChange={(e) => {
                const val = e.target.value;
                const updates: any = { document_type: val };
                if (val === "31" && form.document) updates.document_dv = calcDV(form.document);
                else updates.document_dv = "";
                setForm({ ...form, ...updates });
              }}
            >
              <MenuItem value="13">CC - Cédula</MenuItem>
              <MenuItem value="31">NIT</MenuItem>
              <MenuItem value="22">CE - Cédula Extranjería</MenuItem>
              <MenuItem value="42">DEX - Documento Extranjero</MenuItem>
              <MenuItem value="50">NIT Extranjero</MenuItem>
              <MenuItem value="91">NUIP</MenuItem>
            </TextField>
            <div className="input_row">
              <TextField
                name="document" label="Número documento" required fullWidth
                value={form.document}
                onChange={(e) => {
                  const val = e.target.value;
                  const updates: any = { document: val };
                  if (form.document_type === "31") updates.document_dv = calcDV(val);
                  setForm({ ...form, ...updates });
                }}
              />
              {form.document_type === "31" && (
                <TextField name="document_dv" label="DV" disabled style={{ maxWidth: 70 }} value={form.document_dv} />
              )}
            </div>
          </div>

          <div className="input_group">
            <h2>Nombre</h2>
            <div className="input_row">
              <TextField name="client_name" label="Primer nombre" required fullWidth value={form.client_name} onChange={handleChange} />
              <TextField name="client_name2" label="Segundo nombre" fullWidth value={form.client_name2} onChange={handleChange} />
            </div>
            <div className="input_row">
              <TextField name="client_last_name" label="Primer apellido" required fullWidth value={form.client_last_name} onChange={handleChange} />
              <TextField name="client_last_name2" label="Segundo apellido" fullWidth value={form.client_last_name2} onChange={handleChange} />
            </div>
            <TextField name="business_name" label="Razón social / Nombre negocio" fullWidth value={form.business_name} onChange={handleChange} />
          </div>

          <div className="input_group">
            <h2>Tributario</h2>
            <div className="input_row">
              <TextField name="person_type" label="Tipo persona" select fullWidth value={form.person_type} onChange={handleChange}>
                <MenuItem value="1">Jurídica</MenuItem>
                <MenuItem value="2">Natural</MenuItem>
              </TextField>
              <TextField name="tax_regime" label="Régimen fiscal" select fullWidth value={form.tax_regime} onChange={handleChange}>
                <MenuItem value="O-99">No responsable</MenuItem>
                <MenuItem value="O-13">Gran contribuyente</MenuItem>
                <MenuItem value="O-15">Autorretenedor</MenuItem>
                <MenuItem value="O-23">Agente de retención IVA</MenuItem>
                <MenuItem value="O-47">Régimen simple</MenuItem>
              </TextField>
            </div>
            <TextField name="tax_responsible" label="Responsable IVA" select fullWidth value={form.tax_responsible} onChange={handleChange}>
              <MenuItem value="0">No</MenuItem>
              <MenuItem value="1">Sí</MenuItem>
            </TextField>
          </div>

          <div className="input_group">
            <h2>Contacto y Ubicación</h2>
            <div className="input_row">
              <TextField name="client_phone" label="Teléfono" fullWidth value={form.client_phone} onChange={handleChange} />
              <TextField name="client_email" label="Email" required fullWidth value={form.client_email} onChange={handleChange} />
            </div>
            <TextField name="client_address" label="Dirección" required fullWidth value={form.client_address} onChange={handleChange} />
            <div className="input_row">
              <AutocompleteField
                label="Departamento" endpoint="/clients/locationRegions" fullwidth dynamic max_results={10}
                value={form.location_region_id}
                onChange={(data: any) => setForm({ ...form, location_region_id: data?.value || "", location_city_id: "" })}
              />
              <AutocompleteField
                label="Ciudad" endpoint="/clients/locationCities"
                endpoint_params={{ region_id: form.location_region_id }}
                fullwidth dynamic max_results={10}
                disabled={!form.location_region_id}
                value={form.location_city_id}
                onChange={(data: any) => setForm({ ...form, location_city_id: data?.value || "" })}
              />
            </div>
            <TextField name="postal_code" label="Código postal" fullWidth value={form.postal_code} onChange={handleChange} />
          </div>

          <div className="dialogButtons">
            <Button onClick={onClose} variant="outlined">Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained">Crear Cliente</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
